import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiBatch, unwrapBatch } from '@/api/batch';
import type { LedgerListResponse, Collection, Tag, LedgerType } from '@/api/types';
import { resolveApiPath } from '@/contexts/GoApiContext';

interface UseAttributionBatchParams {
    type: LedgerType;
}

/**
 * Batch response data returned by the hook.
 */
export interface AttributionBatchData {
    ledgers: LedgerListResponse;
    collections: { items: Collection[] };
    tags: { items: Tag[] };
}

/**
 * Batched data load for the Attribution page: all ledgers of the active type
 * (including archived — they are valid attribution members) plus the
 * type-scoped collections and tags, in a single Lambda invocation.
 *
 * Membership per attribution is derived client-side from each ledger's
 * `collectionId` / `tagIds` — no dedicated read endpoint exists.
 *
 * The query key deliberately lives under the `['batch-ledgers']` prefix so
 * the attribution mutations' invalidation set (see
 * `invalidateAttributionCache`) refreshes this query too.
 */
export function useAttributionBatch({ type }: UseAttributionBatchParams) {
    const ledgersPath = `/api/ledgers?type=${type}&limit=0&includeArchived=true`;
    const collectionsPath = resolveApiPath('/api/collections') + `?type=${type}`;
    const tagsPath = resolveApiPath('/api/tags') + `?type=${type}`;

    return useQuery({
        queryKey: ['batch-ledgers', { type, view: 'attribution' }],
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await apiBatch<{
                ledgers: LedgerListResponse;
                collections: { items: Collection[] };
                tags: { items: Tag[] };
            }>([
                { id: 'ledgers', path: ledgersPath },
                { id: 'collections', path: collectionsPath },
                { id: 'tags', path: tagsPath },
            ]);

            return {
                ledgers: unwrapBatch(result.responses.ledgers, 'ledgers'),
                collections: unwrapBatch(result.responses.collections, 'collections'),
                tags: unwrapBatch(result.responses.tags, 'tags'),
            } as AttributionBatchData;
        },
    });
}
